<script setup lang="ts">
import { computed, watch, onBeforeUnmount } from 'vue'

// Canonical modal. Encodes the gold-standard modal UX (see the space-booking
// modal in EventTabSpaces and DESIGN_SYSTEM §10):
//   • header + optional tabs + footer are fixed; only the body scrolls (85vh cap)
//   • Esc closes; the page behind is scroll-locked while open
//   • form/editor modals should NOT close on a backdrop click (pass
//     :close-on-backdrop="false") so a stray click can't discard unsaved input
//   • secondary & destructive actions (Open in X, Delete, Release/auflösen) go
//     in a ⋮ kebab in the #header slot — NOT the footer; the footer keeps only
//     Cancel + the one primary Save (DESIGN_SYSTEM §10). A pure confirm/delete
//     dialog is the exception: its danger action is the footer's primary.
//   • optional in-modal tabs render a compact tab row in the fixed header zone;
//     when tabs are present the height is pinned so switching to a near-empty
//     tab doesn't make the modal jump
//   • z-index scale: 1000 base modal · 1100 confirm-over-modal · 1200 popover
//     · 1300 lightbox. Pass `zIndex` to place a stacked modal.
const props = withDefaults(defineProps<{
  show: boolean
  title?: string
  size?: 'small' | 'default' | 'large' | 'xl'
  /** Close when the backdrop (outside the modal) is clicked. Default FALSE —
   *  modals close only via Cancel/Close/×/Esc, so a stray outside click never
   *  dismisses one. Opt in with :close-on-backdrop="true" for throwaway popovers. */
  closeOnBackdrop?: boolean
  /** Close on the Escape key. Default true. */
  closeOnEsc?: boolean
  /** Lock the page scroll behind the modal while open. Default true. */
  lockScroll?: boolean
  zIndex?: number
  /** Optional in-modal tabs. Pair with v-model:activeTab. */
  tabs?: Array<{ id: string; label: string }>
  activeTab?: string
  /** Pin a stable height (auto-on when tabs are present) so content changes
   *  don't resize the modal. */
  stableHeight?: boolean
  /** The pinned-height cap in px (height: min(85vh, Npx)). Default 640. */
  heightPx?: number
  /** Extra classes on the scrolling body — e.g. "!p-0" for a full-bleed
   *  table/detail body that shouldn't have the default 24px padding. */
  bodyClass?: string
  /** Extra classes on the header bar — e.g. a tinted "bg-amber-50" for a
   *  warning/notice modal. */
  headerClass?: string
  /** Hide the header bar entirely — for crest-style or full-bleed modals that
   *  carry their own in-body title/close. Combine with bodyClass="!p-0". */
  hideHeader?: boolean
  /** Render the body as LOOK-BUT-DO-NOT-TOUCH: every input, select, textarea
   *  and button inside is disabled by the browser, via a bare <fieldset>.
   *
   *  For a user who may read a record but not change it. Opening a detail
   *  modal is a read action, so it must not depend on edit rights — but the
   *  fields inside must not be typeable either, the same way a view-then-edit
   *  modal keeps them inert until you press Edit. One wrapper does it for a
   *  whole modal; the alternative was :disabled on several hundred fields.
   *
   *  The footer slot is deliberately OUTSIDE the fieldset, so Cancel/Close
   *  still work and the caller keeps control of its own Save button. */
  readonly?: boolean
}>(), {
  title: '',
  size: 'default',
  closeOnBackdrop: false,
  closeOnEsc: true,
  lockScroll: true,
  zIndex: 0,
  tabs: () => [],
  activeTab: '',
  stableHeight: false,
  heightPx: 640,
  bodyClass: '',
  headerClass: '',
  hideHeader: false,
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:activeTab', id: string): void
}>()

const hasTabs = computed(() => (props.tabs?.length || 0) > 0)
const pinHeight = computed(() => props.stableHeight || hasTabs.value)
const modalStyle = computed(() => pinHeight.value ? { height: `min(85vh, ${props.heightPx}px)` } : {})

function onBackdrop() {
  if (props.closeOnBackdrop !== false) emit('close')
}

// ── Esc-to-close + background scroll-lock ──────────────────────────────
// A shared ref-count keeps nested modals from unlocking the page early.
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.closeOnEsc !== false) emit('close')
}
// Per-instance flag so we only ever unlock a lock we actually took (and never
// touch the page scroll on an initially-hidden modal).
let didLock = false
watch(() => props.show, (open) => {
  if (open) {
    if (props.closeOnEsc !== false) document.addEventListener('keydown', onKeydown)
    if (props.lockScroll !== false && !didLock) { lockBodyScroll(); didLock = true }
  } else {
    document.removeEventListener('keydown', onKeydown)
    if (didLock) { unlockBodyScroll(); didLock = false }
  }
}, { immediate: true })

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  if (didLock) { unlockBodyScroll(); didLock = false }
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="modal-overlay"
      :style="zIndex ? { zIndex } : {}"
      @click.self="onBackdrop"
    >
      <div
        class="modal"
        :class="{
          'modal-large': size === 'large',
          'modal-xl': size === 'xl',
          'modal-small': size === 'small',
        }"
        :style="modalStyle"
      >
        <!-- Header: custom slot or default title (omitted when hideHeader). -->
        <div v-if="!hideHeader" class="modal-header" :class="headerClass">
          <slot name="header">
            <h2>{{ title }}</h2>
            <button class="btn-close" type="button" @click="emit('close')">×</button>
          </slot>
        </div>

        <!-- Optional in-modal tabs — fixed header zone, body scrolls below. -->
        <div v-if="hasTabs" class="modal-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="bm-modal-tab"
            :class="{ 'bm-modal-tab-active': activeTab === tab.id }"
            @click="emit('update:activeTab', tab.id)"
          >{{ tab.label }}</button>
        </div>

        <!-- Body (scrolls). With tabs, the caller switches panels on activeTab. -->
        <div class="modal-body" :class="bodyClass">
          <!-- A bare fieldset: no border, no padding, and min-width:0 so it
               cannot break a flex/grid child's shrinking (the classic fieldset
               layout trap). It exists only to carry `disabled` down the tree. -->
          <fieldset :disabled="readonly" class="bm-ro-fieldset">
            <slot :active-tab="activeTab" />
          </fieldset>
        </div>

        <!-- Footer (optional) -->
        <div v-if="$slots.footer" class="modal-footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
// Module-scoped scroll-lock ref-count shared by every BaseModal instance, so a
// modal closing over another modal doesn't unlock the page while one is still up.
let openModalCount = 0
let prevBodyOverflow = ''
function lockBodyScroll() {
  try {
    if (openModalCount === 0) { prevBodyOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden' }
    openModalCount++
  } catch { /* no document */ }
}
function unlockBodyScroll() {
  try {
    openModalCount = Math.max(0, openModalCount - 1)
    if (openModalCount === 0) document.body.style.overflow = prevBodyOverflow
  } catch { /* no document */ }
}
</script>

<style>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
}

.modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  margin: auto;
}

.modal-small {
  max-width: 500px;
}

.modal-large {
  max-width: 900px;
}

.modal-xl {
  max-width: 1100px;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

/* In-modal tabs — compact variant of the canonical page tab (DESIGN_SYSTEM §7.1),
   sitting in the fixed header zone so they never scroll with the body. */
.modal-tabs {
  display: flex;
  align-items: stretch;
  gap: 4px;
  padding: 0 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.bm-modal-tab {
  padding: 10px 16px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 6px 6px 0 0;
  margin-bottom: -1px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
}
.bm-modal-tab:hover {
  background: #f9fafb;
}
.bm-modal-tab-active {
  background: #f3f4f6;
  border-bottom-color: #111827;
  color: #111827;
  font-weight: 700;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.btn-close:hover {
  background: #f3f4f6;
}

/* The read-only wrapper must be invisible in layout terms. */
.bm-ro-fieldset {
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
  display: contents;
}
/* A disabled field should read as "not yours to change", not as broken. */
.bm-ro-fieldset:disabled :is(input, select, textarea) {
  background: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
}
</style>
